import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Info } from 'lucide-react';

export default function AnalyticsProductCard({ product, campaigns = [], rank }) {
  const navigate = useNavigate();
  const [imageLoaded, setImageLoaded] = useState(false);

  // Fiyat hesaplama
  const productPrice = product.currentPrice || product.price || 0;
  const productOriginalPrice = product.originalPrice || product.price || 0;

  const productDiscountPercent = productOriginalPrice > 0
    ? ((productOriginalPrice - productPrice) / productOriginalPrice * 100)
    : (product.discount || 0);

  let finalPrice = productPrice;
  let hasDiscount = false;
  let discountSource = null;

  return (
    <article className="analytics-product-card" onClick={() => navigate(`/product/${product.id}`)}>
      {/* Rank Badge */}
      {rank && (
        <div className="rank-badge-wrapper">
          <span className={`rank-badge rank-${rank}`}>#{rank}</span>
        </div>
      )}

      {/* Ürün Görseli */}
      <div className="product-image">
        {!imageLoaded && <div className="image-skeleton"></div>}
        <img
          src={product.image}
          alt={product.name}
          onLoad={() => setImageLoaded(true)}
          style={{
            display: imageLoaded ? 'block' : 'none',
            objectFit: 'contain',
            background: '#fff',
            padding: '8px'
          }}
        />
      </div>

      {/* Ürün Bilgileri */}
      <div className="product-info">
        <div className="product-brand">{product.name}</div>

        <div className="product-desc">
          {product.description && product.description.includes('-') ? (
            product.description.split('\n').slice(0, 2).map((line, index) => (
              <div key={index}>{line}</div>
            ))
          ) : (
            product.description || 'Açıklama yok'
          )}
        </div>

        {product.rating !== 0 && (
          <div className="product-rating">
            <Star size={14} fill="#ffc107" stroke="#ffc107" />
            <span>{product.rating}</span>
          </div>
        )}

        {/* Fiyat */}
        <div className="product-price">
          {hasDiscount && (
            <span className="old-price">
              {productOriginalPrice.toFixed(2)}₺
            </span>
          )}
          <span className="current-price">{finalPrice.toFixed(2)}₺</span>

          {discountSource === 'campaign' && (
            <span className="campaign-label success">🎁 Kampanyalı</span>
          )}

          {discountSource === 'product' && hasDiscount && (
            <span className="campaign-label">💰 İndirimli</span>
          )}
        </div>
      </div>

      <style jsx>{`
        .analytics-product-card {
          background: #fff;
          border-radius: 12px;
          overflow: hidden;
          position: relative;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
          transition: all 0.3s;
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .analytics-product-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0,0,0,0.1);
        }

        /* ==================== RANK BADGE ==================== */
        .rank-badge-wrapper {
          position: absolute;
          top: -4px;
          left: -4px;
          z-index: 10;
        }

        .rank-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          font-weight: 700;
          font-size: 14px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        }

        .rank-1 {
          background: linear-gradient(135deg, #E5332A, #ff5a50);
          color: white;
        }

        .rank-2 {
          background: linear-gradient(135deg, #FFC107, #FFD700);
          color: white;
        }

        .rank-3 {
          background: linear-gradient(135deg, #20C997, #28E5B0);
          color: white;
        }

        .rank-badge:not(.rank-1):not(.rank-2):not(.rank-3) {
          background: linear-gradient(135deg, #868e96, #adb5bd);
          color: white;
        }

        /* ==================== DISCOUNT BADGE ==================== */
        .discount-badge {
          position: absolute;
          top: 8px;
          right: 8px;
          background: #E5332A;
          color: #fff;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 700;
          z-index: 2;
          display: flex;
          align-items: center;
          gap: 3px;
        }

        .campaign-indicator {
          font-size: 10px;
          animation: bounce 1s infinite;
        }

        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-2px); }
        }

        /* ==================== PRODUCT IMAGE ==================== */
        .product-image {
          width: 100%;
          aspect-ratio: 1;
          overflow: hidden;
          background: #f8f9fa;
          position: relative;
        }

        .product-image img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          transition: transform 0.4s;
          padding: 8px;
          background: #fff;
        }

        .analytics-product-card:hover .product-image img {
          transform: scale(1.08);
        }

        .image-skeleton {
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: loading 1.5s infinite;
        }

        @keyframes loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        /* ==================== PRODUCT INFO ==================== */
        .product-info {
          padding: 12px;
          display: flex;
          flex-direction: column;
          flex: 1;
        }

        .product-brand {
          color: #868e96;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          margin-bottom: 4px;
          letter-spacing: 0.5px;
        }

        .product-desc {
          color: #2B2B2B;
          font-size: 0.8rem;
          margin-bottom: 0.75rem;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          min-height: 2.2em;
        }

        .product-rating {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          margin-bottom: 10px;
          color: #2B2B2B;
        }

        .product-rating span {
          font-weight: 600;
        }

        /* ==================== PRICE ==================== */
        .product-price {
          display: flex;
          flex-direction: column;
          gap: 2px;
          margin-top: auto;
        }

        .old-price {
          color: #868e96;
          font-size: 11px;
          text-decoration: line-through;
          font-weight: 500;
        }

        .current-price {
          color: #E5332A;
          font-size: 18px;
          font-weight: 800;
          line-height: 1;
        }

        .campaign-label {
          font-size: 9px;
          font-weight: 700;
          padding: 3px 8px;
          border-radius: 4px;
          display: inline-block;
          margin-top: 4px;
          border: 1px solid;
          align-self: flex-start;
        }

        .campaign-label.success {
          background: rgba(6, 214, 160, 0.1);
          color: #06d6a0;
          border-color: rgba(6, 214, 160, 0.3);
        }

        .campaign-label:not(.success) {
          background: rgba(229, 51, 42, 0.1);
          color: #E5332A;
          border-color: rgba(229, 51, 42, 0.3);
        }

        /* ==================== RESPONSIVE ==================== */
        @media (max-width: 1200px) {
          .product-info {
            padding: 10px;
          }

          .current-price {
            font-size: 16px;
          }

          .rank-badge {
            width: 36px;
            height: 36px;
            font-size: 13px;
          }
        }

        @media (max-width: 768px) {
          .product-desc {
            -webkit-line-clamp: 1;
            font-size: 0.75rem;
          }

          .current-price {
            font-size: 14px;
          }

          .rank-badge {
            width: 32px;
            height: 32px;
            font-size: 12px;
          }

          .rank-badge-wrapper {
            top: -6px;
            left: -6px;
          }
        }

        @media (max-width: 480px) {
          .rank-badge {
            width: 28px;
            height: 28px;
            font-size: 11px;
          }
        }
      `}</style>
    </article>
  );
}